import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid,
  TextField, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Switch, FormControlLabel, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import SecaoFormulario from '../components/SecaoFormulario';
import AcessoNegado from '../components/AcessoNegado';
import EmDesenvolvimento from '../components/EmDesenvolvimento';

const ALERTA_CONFIG = {
  sem_gestor: { label: 'Sem gestor definido', cor: '#DC2626' },
  multiplos_gestores: { label: 'Mais de um gestor apontando pra cá', cor: '#D97706' },
};

const FORM_VAZIO = { nome: '', descricao: '', ativa: true };

const TIPO_HISTORICO = {
  criada: { cor: '#13375F', fundo: '#E3ECF6', Icone: AddCircleOutlineIcon },
  entrada: { cor: '#3B6D11', fundo: '#EAF3DE', Icone: PersonAddAlt1Icon },
  saida: { cor: '#A32D2D', fundo: '#FCEBEB', Icone: PersonRemoveIcon },
};

function descreverMovimento(m) {
  if (m.tipo === 'criada') return 'Célula criada';
  if (m.tipo === 'entrada') {
    return `${m.membro_nome || 'Alguém'} entrou na célula${m.outra_celula_nome ? ` (veio de ${m.outra_celula_nome})` : ''}`;
  }
  return `${m.membro_nome || 'Alguém'} saiu da célula${m.outra_celula_nome ? ` (foi para ${m.outra_celula_nome})` : ''}`;
}

const importado = (m) => (m.observacao || '').startsWith('Importado');

function formatarQuando(m) {
  const d = new Date(m.quando);
  // Registros importados do histórico antigo só têm a data (sem hora certa).
  return importado(m)
    ? d.toLocaleDateString('pt-BR')
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function Celulas() {
  const { isSuperAdmin, isDiretor, isGestor, perfil, moduloAtivo, getPrevisao, temAcesso } = useAuth();
  const moduloLiberado = moduloAtivo('celulas');
  const podeMoverCelula = isSuperAdmin || isDiretor;
  const podeVerHistorico = isSuperAdmin || isDiretor || temAcesso('celulas');
  const podeVer = isGestor || perfil?.perfil === 'consultor' || temAcesso('celulas');

  const [celulas, setCelulas] = useState([]);
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandida, setExpandida] = useState(null);
  const [sucesso, setSucesso] = useState('');

  const [abrirModal, setAbrirModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [abrirMover, setAbrirMover] = useState(null);
  const [membroParaMover, setMembroParaMover] = useState('');
  const [celulaDestino, setCelulaDestino] = useState('');
  const [observacaoMover, setObservacaoMover] = useState('');
  const [erroMover, setErroMover] = useState('');
  const [movendo, setMovendo] = useState(false);

  const [historicoDe, setHistoricoDe] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState('');

  const abrirHistorico = (celula) => {
    setHistoricoDe(celula);
    setHistorico([]);
    setErroHistorico('');
    setCarregandoHistorico(true);
    api.get(`/celulas/${celula.id}/historico/`)
      .then((res) => setHistorico(res.data))
      .catch((err) => setErroHistorico(err?.response?.data?.detail || 'Não foi possível carregar o histórico. Atualize a página e tente de novo.'))
      .finally(() => setCarregandoHistorico(false));
  };

  const carregar = () => {
    setLoading(true);
    Promise.all([
      api.get('/celulas/'),
      api.get('/membros-equipe/'),
    ]).then(([cel, membrosRes]) => {
      setCelulas(cel.data);
      setMembros(membrosRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { if (podeVer && moduloLiberado) carregar(); else setLoading(false); }, [podeVer, moduloLiberado]);

  const handleAbrirNova = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setErro('');
    setAbrirModal(true);
  };

  const handleAbrirEditar = (celula) => {
    setEditando(celula);
    setForm({ nome: celula.nome, descricao: celula.descricao || '', ativa: celula.ativa });
    setErro('');
    setAbrirModal(true);
  };

  const handleSalvar = async () => {
    setErro('');
    if (!form.nome.trim()) {
      setErro('O nome da célula é obrigatório.');
      return;
    }
    setSalvando(true);
    try {
      if (editando) {
        await api.patch(`/celulas/${editando.id}/`, form);
        setSucesso('Célula atualizada com sucesso!');
      } else {
        await api.post('/celulas/', form);
        setSucesso('Célula criada com sucesso!');
      }
      setAbrirModal(false);
      carregar();
    } catch (err) {
      const detalhe = err?.response?.data;
      const primeiroCampo = detalhe && typeof detalhe === 'object' ? Object.values(detalhe)[0] : null;
      setErro(
        (Array.isArray(primeiroCampo) ? primeiroCampo[0] : primeiroCampo) ||
        detalhe?.detail ||
        'Erro ao salvar célula.'
      );
    } finally {
      setSalvando(false);
    }
  };

  const handleAbrirMover = (celula) => {
    setAbrirMover(celula);
    setMembroParaMover('');
    setCelulaDestino('');
    setObservacaoMover('');
    setErroMover('');
  };

  const handleConfirmarMover = async () => {
    setErroMover('');
    if (!membroParaMover) {
      setErroMover('Escolha quem mover.');
      return;
    }
    if (!celulaDestino) {
      setErroMover('Escolha a célula de destino.');
      return;
    }
    setMovendo(true);
    try {
      const membro = membros.find((c) => c.id === membroParaMover);
      await api.post(`/membros-equipe/${membroParaMover}/mover_celula/`, {
        celula: celulaDestino,
        observacao: observacaoMover,
      });
      setSucesso(`${membro?.nome_completo || 'Membro da equipe'} movido(a) de célula com sucesso!`);
      setAbrirMover(null);
      carregar();
    } catch (err) {
      const detalhe = err?.response?.data;
      setErroMover(detalhe?.error || detalhe?.detail || 'Erro ao mover membro da equipe.');
    } finally {
      setMovendo(false);
    }
  };

  if (!moduloLiberado) {
    return <EmDesenvolvimento previsao={getPrevisao('celulas')} />;
  }

  if (!podeVer) {
    return <AcessoNegado />;
  }

  if (loading) return <Carregando />;

  return (
    <Box>
      <PageHeader
        titulo="Células"
        acoes={isSuperAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAbrirNova} sx={{ backgroundColor: '#13375F' }}>
            Nova Célula
          </Button>
        )}
      />

      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}

      {celulas.length === 0 ? (
        <Card><CardContent>
          <Typography color="text.secondary" textAlign="center">Nenhuma célula cadastrada.</Typography>
        </CardContent></Card>
      ) : (
        <Grid container spacing={2}>
          {celulas.map((cel) => {
            const alerta = ALERTA_CONFIG[cel.alerta_gerencia];
            const aberta = expandida === cel.id;
            return (
              <Grid item xs={12} sm={6} md={4} key={cel.id}>
                <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                  <Box sx={{ backgroundColor: '#13375F', px: 2.2, py: 1.3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 14 }}>{cel.nome}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.2 }}>
                      {podeVerHistorico && (
                        <IconButton size="small" title="Ver histórico" aria-label={`Ver histórico da ${cel.nome}`} onClick={() => abrirHistorico(cel)} sx={{ color: 'white', p: 0.4 }}>
                          <HistoryIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      )}
                      {podeMoverCelula && cel.membros_nomes?.length > 0 && (
                        <IconButton size="small" onClick={() => handleAbrirMover(cel)} sx={{ color: 'white', p: 0.4 }}>
                          <SwapHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      {isSuperAdmin && (
                        <IconButton size="small" onClick={() => handleAbrirEditar(cel)} sx={{ color: 'white', p: 0.4 }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 2.2 }}>
                    {alerta && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, backgroundColor: `${alerta.cor}15`, color: alerta.cor, px: 1.2, py: 0.7, borderRadius: 1.5, mb: 1.5, fontSize: 12, fontWeight: 600 }}>
                        <WarningAmberIcon sx={{ fontSize: 15 }} />
                        {alerta.label}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                      <PersonIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                      <Typography variant="body2">
                        <strong>Gerente:</strong> {cel.gerente_nome || '—'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <GroupsIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        <Typography variant="body2">{cel.total_membros_equipe} membro(s)</Typography>
                      </Box>
                      {!cel.ativa && (
                        <Box sx={{ backgroundColor: '#f1f3f6', color: '#64748b', px: 0.9, py: 0.2, borderRadius: 1, fontSize: 11, fontWeight: 700 }}>
                          INATIVA
                        </Box>
                      )}
                    </Box>

                    {cel.membros_nomes?.length > 0 && (
                      <>
                        <Button
                          size="small"
                          endIcon={aberta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={() => setExpandida(aberta ? null : cel.id)}
                          sx={{ mt: 1, color: '#13375F', px: 0 }}
                        >
                          {aberta ? 'Ocultar time' : 'Ver time'}
                        </Button>
                        {aberta && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {cel.membros_nomes.map((nome, i) => (
                              <Box key={i} sx={{ backgroundColor: '#f1f3f6', color: '#1a1a2e', px: 1, py: 0.3, borderRadius: 1, fontSize: 11.5, fontWeight: 600 }}>
                                {nome}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={abrirModal} onClose={() => setAbrirModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{editando ? 'Editar célula' : 'Nova célula'}</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          {editando ? 'Atualize os dados desta célula' : 'Cadastre uma nova célula na estrutura organizacional'}
        </Typography>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <SecaoFormulario titulo="Dados da célula" icone={<AccountTreeOutlinedIcon sx={{ fontSize: 17, color: '#13375F' }} />} cor="#13375F">
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: Célula 1 (Scaramello)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Descrição" multiline rows={3}
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.ativa} onChange={(e) => setForm({ ...form, ativa: e.target.checked })} />}
                label="Célula ativa"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                Células inativas somem das listas de seleção, mas o histórico é mantido.
              </Typography>
            </Grid>
          </SecaoFormulario>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModal(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvar} disabled={salvando} sx={{ backgroundColor: '#13375F' }}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(historicoDe)} onClose={() => setHistoricoDe(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Histórico — {historicoDe?.nome}</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          Criação da célula e quem entrou ou saiu, do mais recente para o mais antigo.
        </Typography>
        <DialogContent>
          {carregandoHistorico && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
          {erroHistorico && <Alert severity="error">{erroHistorico}</Alert>}
          {!carregandoHistorico && !erroHistorico && historico.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Ainda não há movimentações registradas para esta célula.
            </Typography>
          )}
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {historico.map((m) => {
              const t = TIPO_HISTORICO[m.tipo] || TIPO_HISTORICO.criada;
              return (
                <Box component="li" key={m.id} sx={{ display: 'flex', gap: 1.5, py: 1.3, borderTop: '1px solid #eef0f3', '&:first-of-type': { borderTop: 'none' } }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: t.fundo, color: t.cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <t.Icone sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{descreverMovimento(m)}</Typography>
                    {m.observacao && !importado(m) && (
                      <Typography sx={{ fontSize: 13, color: '#475569' }}>{m.observacao}</Typography>
                    )}
                    <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
                      {formatarQuando(m)} · {m.feito_por_nome ? `por ${m.feito_por_nome}` : 'autor não registrado (registro antigo)'}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoricoDe(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(abrirMover)} onClose={() => setAbrirMover(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mover membro da equipe — {abrirMover?.nome}</DialogTitle>
        <DialogContent>
          {erroMover && <Alert severity="error" sx={{ mb: 2 }}>{erroMover}</Alert>}
          <TextField
            fullWidth select label="Quem mover" sx={{ mt: 1, mb: 2 }}
            value={membroParaMover}
            onChange={(e) => setMembroParaMover(e.target.value)}
          >
            {membros.filter((c) => c.celula?.id === abrirMover?.id).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.nome_completo}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth select label="Nova célula" sx={{ mb: 2 }}
            value={celulaDestino}
            onChange={(e) => setCelulaDestino(e.target.value)}
          >
            {celulas.filter((c) => c.id !== abrirMover?.id).map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth label="Observação (opcional)" multiline rows={2}
            value={observacaoMover}
            onChange={(e) => setObservacaoMover(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirMover(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarMover} disabled={movendo} sx={{ backgroundColor: '#13375F' }}>
            {movendo ? 'Movendo...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
